// src/components/layout/ProviderLayout.jsx
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const ProviderLayout = ({ children }) => {
  const { i18n, t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = React.useState(user?.companyName || '');

  // Fetch live company name if user is an admin
  React.useEffect(() => {
    const fetchLiveCompanyName = async () => {
      if (user?.role === 'admin') {
        try {
          const { dashboardService } = await import('../../services/dashboardServices');
          const response = await dashboardService.getProviderMetrics();
          if (response.success && response.data?.companyInfo?.name) {
            setCompanyName(response.data.companyInfo.name);
          }
        } catch (error) {
          console.warn('Could not fetch live company name for layout');
        }
      } else {
        setCompanyName(user?.companyName || '');
      }
    };

    fetchLiveCompanyName();
  }, [user]);

  // Déterminer quelle page est active
  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Force a page reload to ensure complete state reset
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation to login if logout fails
      window.location.href = '/login';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-700 text-white p-4 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold">EBIOS RM - Entreprise</h1>
          <div className="flex items-center space-x-4">
            {/* User info */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center bg-blue-800 px-3 py-1.5 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 mr-2">
                  <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{user?.name || 'Provider'}</span>
              </div>

              {/* Company name if available */}
              {companyName && (
                <div className="flex items-center bg-blue-800 px-3 py-1.5 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 mr-2">
                    <path fillRule="evenodd" d="M4.5 2.25a.75.75 0 000 1.5v16.5h-.75a.75.75 0 000 1.5h16.5a.75.75 0 000-1.5h-.75V3.75a.75.75 0 000-1.5h-15zM9 6a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5H9zm-.75 3.75A.75.75 0 019 9h1.5a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75zM9 12a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5H9zm3.75-5.25A.75.75 0 0113.5 6H15a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75zM13.5 9a.75.75 0 000 1.5H15A.75.75 0 0015 9h-1.5zm-.75 3.75a.75.75 0 01.75-.75H15a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">{companyName}</span>
                </div>
              )}
            </div>

            {/* Language Toggle Button */}
            <div className="flex items-center bg-blue-800 rounded-full p-1 transition-all duration-300">
              <button
                onClick={() => i18n.changeLanguage('fr')}
                className={`flex items-center px-2 py-1 text-xs rounded-full transition-all duration-300 ${i18n.language === 'fr'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-blue-200 hover:text-white hover:bg-blue-600'
                  }`}
                title="Français"
              >
                <span className="fi fi-fr mr-1" style={{ fontSize: '0.875rem' }}></span>
                <span className="font-medium">FR</span>
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`flex items-center px-2 py-1 text-xs rounded-full transition-all duration-300 ${i18n.language === 'en'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-blue-200 hover:text-white hover:bg-blue-600'
                  }`}
                title="English"
              >
                <span className="fi fi-gb mr-1" style={{ fontSize: '0.875rem' }}></span>
                <span className="font-medium">EN</span>
              </button>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 focus:outline-none"
              title={t('common.logout') || 'Logout'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 mr-1">
                <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9a.75.75 0 01-1.5 0V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
              <span className="hidden sm:inline">{t('common.logout') || 'Logout'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-md">
        <div className="container mx-auto">
          <ul className="flex">
            <li>
              <Link
                to="/provider/dashboard"
                className={`px-4 py-3 inline-block ${isActive('/provider/dashboard')
                  ? 'text-blue-700 border-b-2 border-blue-700 font-medium'
                  : 'text-gray-600 hover:text-blue-600'
                  }`}
              >
                Dashboard
              </Link>
            </li>
            <li>
              <Link
                to="/provider/users"
                className={`px-4 py-3 inline-block ${isActive('/provider/users')
                  ? 'text-blue-700 border-b-2 border-blue-700 font-medium'
                  : 'text-gray-600 hover:text-blue-600'
                  }`}
              >
                Utilisateurs
              </Link>
            </li>
            <li>
              <Link
                to="/provider/analyses"
                className={`px-4 py-3 inline-block ${isActive('/provider/analyses')
                  ? 'text-blue-700 border-b-2 border-blue-700 font-medium'
                  : 'text-gray-600 hover:text-blue-600'
                  }`}
              >
                Analyses
              </Link>
            </li>
            <li>
              <Link
                to="/provider/activities"
                className={`px-4 py-3 inline-block ${isActive('/provider/activities')
                  ? 'text-blue-700 border-b-2 border-blue-700 font-medium'
                  : 'text-gray-600 hover:text-blue-600'
                  }`}
              >
                Logs d'activité
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto py-6">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-200 p-4 text-center text-gray-600">
        EBIOS RM - Entreprise © 2025
      </footer>
    </div>
  );
};

export default ProviderLayout;